import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { DEFAULT_PAGINATION_SIZE } from "../components/common/Constants";
import { GET_NOTIFICATIONS_LIST } from "../graphql/notification";
import dayjs from "utils/dayjs";
import { surnameFirstName } from "utils/fullName";

interface Data {
  id: string;
  title: string;
  body: string | null;
  userName: string;
  type: string | null;
  date: Date;
  daysFromToday: string;
  toScreen: string;
  dataId: string;
  actorName: string | null;
  cardId: string | null;
  panelScreen: string | null;
  status: string;
}

interface NotificationQuery {
  first?: number;
  after?: string;
  status: string;
  refetchIndex?: number;
}

export function useNotifications(status: string, refetchIndex: number) {
  const [fetchState, setFetchState] = useState<{
    variables: NotificationQuery;
    isFetchingMore: boolean;
  }>({
    variables: {
      status: status,
      first: DEFAULT_PAGINATION_SIZE,
      refetchIndex,
    },
    isFetchingMore: false,
  });

  const setIsFetchingMore = (isFetchingMore: boolean) => {
    setFetchState((prevState) => ({ ...prevState, isFetchingMore }));
  };

  const setVariables = (variables: NotificationQuery) => {
    setFetchState((prevState) => ({ ...prevState, variables }));
  };

  const [formattedNotifications, setFormattednotifications] = useState<Data[]>(
    [],
  );
  const { data: notificationData, loading: loadingNotifications } = useQuery(
    GET_NOTIFICATIONS_LIST,
    {
      variables: fetchState.variables,
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const newNotifications = data.myNotificationsScroll.edges.map(
          (item: any) => ({
            id: item.node.id,
            title: item.node.title,
            body: item.node.body,
            userName: surnameFirstName(item.node.toUser),
            type: item.node.type,
            date: new Date(item.node.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
            daysFromToday: dayjs(item.node.createdAt).fromNow(),
            dataId: item.node.dataId,
            toScreen: item.node.toScreen,
            actorName: item.node.actorName,
            cardId: item.node.cardId,
            panelScreen: item.node.panelScreen,
            status: item.node.status,
          }),
        );

        setFormattednotifications((prevNotifications) => [
          ...(fetchState.isFetchingMore ? prevNotifications : []),
          ...newNotifications,
        ]);

        setFetchState((prevState) => ({ ...prevState, isFetchingMore: false }));
      },
    },
  );

  // Function to fetch more notifications (key is to guaruntee a change in variables to guarantee a refetch)
  useEffect(() => {
    setFetchState((prevState) => ({
      ...prevState,
      variables: {
        ...prevState.variables,
        first:
          formattedNotifications.length > DEFAULT_PAGINATION_SIZE
            ? formattedNotifications.length
            : DEFAULT_PAGINATION_SIZE,
        refetchIndex,
        status: status,
        after: undefined,
      },
    }));
  }, [status, refetchIndex]);

  return {
    formattedNotifications,
    isFetchingMore: fetchState.isFetchingMore,
    variables: fetchState.variables,
    hasNextPage: notificationData?.myNotificationsScroll.pageInfo
      .hasNextPage as string,
    endCursor: notificationData?.myNotificationsScroll.pageInfo.endCursor,
    total: notificationData?.myNotificationsScroll.pageInfo.total,
    setIsFetchingMore,
    setVariables,
    loadingNotifications,
  };
}
